import {
  DeleteTwoTone,
  EditTwoTone,
  PlusOutlined,
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Button,
  Table,
  Tooltip,
  Form,
  notification,
  PageHeader,
  Row,
  Col,
  Switch,
  Select,
} from "antd";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import IconNotFound from "../../static/icon/IconNotFound";
import DeleteAction from "../../Tools/DeleteAction";

import { useAuth } from "../../context/auth";
import LandlordDropdown from "../Dropdown/LandlordDropdown";
import IconLand from "../../static/icon/IconLand";

const Party = () => {
  const history = useHistory();
  const { accessToken } = useAuth();

  const [form] = Form.useForm();
  const selectedMerchants = Form.useWatch('merchantIds', form);

  const [loading, setLoading] = useState(false);

  const [update, setUpdate] = useState(0);
  const [total, setTotal] = useState(0);
  const [partys, setPartys] = useState([]);
  const [search, setSearch] = useState({
    pageValue: history.location.pageValue ?? {},
    searchValue: history.location.searchValue ?? {},
  });

  useEffect(() => {
    form.setFieldsValue(search.searchValue);
  }, [form, search]);

  const [currentParty, setCurrentParty] = useState(null);

  useEffect(() => {
    async function getPartys({ pageValue, searchValue }) {
      setLoading(true);

      const url = new URL('/api/party', window.location);
      if (searchValue.landlordIds) for (const landlordId of searchValue.landlordIds) url.searchParams.append('landlordId', landlordId);
      if (typeof searchValue.active === 'boolean') url.searchParams.append('active', searchValue.active);

      if (pageValue.sort) url.searchParams.append('sort', pageValue.sort);
      if (pageValue.order) url.searchParams.append('order', pageValue.order);
      if (pageValue.page) url.searchParams.append('page', pageValue.page);
      if (pageValue.size) url.searchParams.append('size', pageValue.size);

      const res = await fetch(url, {
        headers: { 'X-ACCESS-TOKEN': accessToken },
      });
      if (res.ok) {
        const { total, partys } = await res.json();
        setTotal(total);
        setPartys(partys);
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }

      setLoading(false);
    };

    getPartys(search);
  }, [search, accessToken, update]);

  const columns = [
    {
      title: "Name",
      dataIndex: "nameEn",
      key: "nameEn",
    },
    {
      title: "Site Name",
      dataIndex: "landlordNameEns",
      key: "landlordNameEns",
      render: (landlordNameEns) => (
        <Tooltip title={landlordNameEns.join(', ')}>
          {landlordNameEns.length} landlord(s)
        </Tooltip>
      ),
    },
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      render: (active, party) => (
        <Switch
          checked={active}
          onChange={async (active) => {
            const url = new URL('/api/party/' + party.id, window.location);

            const res = await fetch(url, {
              method: 'PATCH',
              headers: {
                'X-ACCESS-TOKEN': accessToken,
                'Content-type': 'application/json',
              },
              body: JSON.stringify({ active }),
            });
            if (res.ok) {
              notification.success({
                message: "Success",
                description: "Successfully saved data.",
              });
            } else {
              notification["error"]({
                message: "Something went wrong !",
                description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
              });
            }
            setUpdate(update + 1);
          }}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, party) => (
        <span style={{ whiteSpace: "nowrap" }}>
          <Tooltip placement="bottom" title="View" className="mx-1">
            <EditTwoTone
              style={{
                backgroundColor: "#F3F6F9",
                padding: 6,
                borderRadius: 6,
              }}
              onClick={() => {
                history.push({
                  pathname: `/party/${party.id}`,
                  searchValue: search.searchValue,
                  pageValue: search.pageValue,
                });
              }}
              twoToneColor="#00C9D6"
            />
          </Tooltip>

          {!party.active && (
            <Tooltip placement="bottom" title="Delete" className="mx-1">
              <DeleteTwoTone
                style={{
                  backgroundColor: "#F3F6F9",
                  padding: 6,
                  borderRadius: 6,
                }}
                onClick={() => setCurrentParty(party)}
                twoToneColor="red"
              />
            </Tooltip>
          )}
        </span>
      ),
    },
  ].filter(col => !col.hidden);

  return (
    <>
      <div className="flex justify-between items-center mt-2 space-x-4">
        <PageHeader
          style={{ padding: 0, width: "100%" }}
          title={
            <span className="flex items-center mt-2">
              <IconLand /> &nbsp;&nbsp;Group
            </span>
          }
        />

        <Button
          className="mt-2 flex items-center"
          icon={<PlusOutlined />}
          type="primary"
          shape="round"
          onClick={() => {
            history.push({
              pathname: `/party/new`,
              searchValue: search.searchValue,
              pageValue: search.pageValue,
            });
          }}
        >
          New Group
        </Button>
      </div>
      <div className="content">
        <div className="">
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => setSearch(search => ({
              pageValue: { ...search.pageValue, page: 1 },
              searchValue: values,
            }))}
            onReset={() => form.submit()}
          >
            <Row align="middle" gutter={16}>
              <Col className="gutter-row" span={6}>
                <Form.Item
                  label="Site Name"
                  name="landlordIds"
                  colon={false}
                  initialValue={[]}
                >
                  <LandlordDropdown
                    allowClear
                    allowSearch
                    mode="multiple"
                    merchantIds={selectedMerchants}
                    setSelectedLandlords={(landlordIds) => form.setFieldsValue({ landlordIds })}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Status"
                  name="active"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    showSearch
                    allowClear
                    options={[
                      { value: null, label: 'Any Status' },
                      { value: true, label: 'Active' },
                      { value: false, label: 'Inactive' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={14}>
                <div className="flex">
                  <Button
                    className="w-28"
                    type="primary"
                    shape="round"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                  >
                    Search
                  </Button>

                  <Button
                    className="w-28 ml-2"
                    type="primary"
                    ghost
                    shape="round"
                    htmlType="reset"
                    icon={<SyncOutlined />}
                  >
                    Clear Data
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
      <div className="content">
        <Table
          className="inbox_table"
          locale={{ emptyText: <IconNotFound /> }}
          rowKey={(party) => party.id}
          loading={loading}
          columns={columns}
          dataSource={partys}
          pagination={{
            size: "small",
            current: search.pageValue.page,
            pageSize: search.pageValue.size,
            defaultPageSize: 10,
            total,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: (total, range) => ` ${range[0]}-${range[1]} of ${total} `,
          }}
          onChange={(pagination, _, sorter) => setSearch(search => ({
            pageValue: {
              page: pagination.current ?? 1,
              size: pagination.pageSize ?? 10,
              sort: sorter.columnKey,
              order: sorter.order === 'ascend' ? 'asc' : 'desc',
            },
            searchValue: search.searchValue,
          }))}
        />
      </div>

      <DeleteAction
        openModalActionDelete={!!currentParty}
        name="Group"
        type="party"
        editRecord={currentParty}
        onCancel={() => setCurrentParty(null)}
        onOk={() => {
          setCurrentParty(null);
          setUpdate(update + 1);
        }}
      />
    </>
  );
};

export default Party;
